import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLElement} ctaMount - Mount point for mirrored main CTA
 */

/**
 * Mobile sticky bar that mirrors the main buy button and triggers it on click.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'ctaMount'];

  /** @type {MediaQueryList} */
  #mobileMediaQuery = window.matchMedia('(max-width: 749px)');

  /** @type {HTMLElement | null} */
  #observedBuyButtons = null;

  /** @type {HTMLElement | null} */
  #observedTarget = null;

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {MutationObserver | null} */
  #mainButtonObserver = null;

  /** @type {number | null} */
  #mirrorFrame = null;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
    }

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    document.addEventListener(ThemeEvents.variantUpdate, this.#scheduleMirror, { signal });
    document.addEventListener(ThemeEvents.variantSelected, this.#scheduleMirror, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#scheduleMirror, { signal });
    this.#mobileMediaQuery.addEventListener('change', this.#handleMobileMediaChange, { signal });
    window.addEventListener('resize', this.#handleResize, { signal, passive: true });

    this.#scheduleMirror();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#mainButtonObserver?.disconnect();
    this.#abortController.abort();
    if (this.#mirrorFrame) cancelAnimationFrame(this.#mirrorFrame);
  }

  /**
   * Bottom inset covered by the app nav (or safe area). Buttons in this zone
   * are treated as not visible so the sticky bar stays available.
   * @returns {number}
   */
  #getBottomObscuredHeight() {
    if (!this.#mobileMediaQuery.matches) return 0;

    const nav = document.querySelector('.mobile-bottom-nav');
    if (!(nav instanceof HTMLElement)) return 0;

    const height = nav.getBoundingClientRect().height;
    if (height > 0) return Math.ceil(height);

    const rootStyles = getComputedStyle(document.documentElement);
    const offset = rootStyles.getPropertyValue('--mobile-bottom-nav-offset').trim();
    if (!offset) return 0;

    const probe = document.createElement('div');
    probe.style.cssText = `position:absolute;visibility:hidden;height:${offset}`;
    document.body.appendChild(probe);
    const px = probe.getBoundingClientRect().height;
    probe.remove();
    return px > 0 ? Math.ceil(px) : 0;
  }

  #createBuyButtonsObserver() {
    const bottomInset = this.#getBottomObscuredHeight();

    return new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;
        this.#syncStickyVisibility(/** @type {HTMLElement} */ (entry.target), entry.isIntersecting);
      },
      {
        // Shrink the "visible" area so content under the bottom nav does not count.
        rootMargin: `0px 0px -${bottomInset}px 0px`,
        threshold: 0,
      }
    );
  }

  #setupIntersectionObserver() {
    const buyButtonsBlock = this.#getBuyButtonsBlock();
    if (!buyButtonsBlock) return;

    const observeTarget = this.#getVisibilityTarget(buyButtonsBlock) ?? buyButtonsBlock;
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');

    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#buyButtonsIntersectionObserver = this.#createBuyButtonsObserver();

    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          this.#hiddenByBottom = false;
          const target = this.#observedTarget ?? buyButtonsBlock;
          this.#syncStickyVisibility(target, this.#isTargetInView(target));
        }
      },
      { rootMargin: '200px 0px 0px 0px' }
    );

    this.#observedBuyButtons = buyButtonsBlock;
    this.#observedTarget = observeTarget;
    this.#buyButtonsIntersectionObserver.observe(observeTarget);

    if (footer) {
      this.#mainBottomObserver.observe(footer);
    }

    this.#setupMainButtonObserver(buyButtonsBlock);
    this.#syncStickyVisibility(observeTarget, this.#isTargetInView(observeTarget));
  }

  /**
   * Prefer the actual ATC button so a tall buy-buttons block does not keep
   * the sticky hidden while the CTA itself is still off-screen / under the nav.
   * @param {HTMLElement} buyButtonsBlock
   * @returns {HTMLElement | null}
   */
  #getVisibilityTarget(buyButtonsBlock) {
    const button = this.#getMainAddToCartButtonFrom(buyButtonsBlock);
    return button ?? buyButtonsBlock;
  }

  /**
   * @param {HTMLElement} buyButtonsBlock
   */
  #setupMainButtonObserver(buyButtonsBlock) {
    this.#mainButtonObserver?.disconnect();

    this.#mainButtonObserver = new MutationObserver(this.#scheduleMirror);
    this.#mainButtonObserver.observe(buyButtonsBlock, {
      subtree: true,
      childList: true,
      attributes: true,
      characterData: true,
      attributeFilter: ['disabled', 'class', 'aria-label', 'aria-disabled'],
    });
  }

  #scheduleMirror = () => {
    if (this.#mirrorFrame) cancelAnimationFrame(this.#mirrorFrame);
    this.#mirrorFrame = requestAnimationFrame(() => {
      this.#mirrorFrame = null;
      this.#ensureBuyButtonsObserver();
      this.#mirrorMainButton();
    });
  };

  #handleResize = () => {
    this.#rebuildBuyButtonsObserver();
  };

  /**
   * Rebuild observer when bottom inset changes (nav/safe-area/orientation).
   */
  #rebuildBuyButtonsObserver() {
    const buyButtonsBlock = this.#getBuyButtonsBlock();
    if (!buyButtonsBlock) return;

    const observeTarget = this.#getVisibilityTarget(buyButtonsBlock) ?? buyButtonsBlock;

    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#buyButtonsIntersectionObserver = this.#createBuyButtonsObserver();
    this.#observedBuyButtons = buyButtonsBlock;
    this.#observedTarget = observeTarget;
    this.#buyButtonsIntersectionObserver.observe(observeTarget);
    this.#syncStickyVisibility(observeTarget, this.#isTargetInView(observeTarget));
  }

  /**
   * Rebinds observers when buy-buttons is replaced after a variant update.
   */
  #ensureBuyButtonsObserver() {
    const buyButtonsBlock = this.#getBuyButtonsBlock();
    if (!buyButtonsBlock) return;

    const observeTarget = this.#getVisibilityTarget(buyButtonsBlock) ?? buyButtonsBlock;
    const sameBlock = buyButtonsBlock === this.#observedBuyButtons;
    const sameTarget = observeTarget === this.#observedTarget;

    if (sameBlock && sameTarget && this.#buyButtonsIntersectionObserver) return;

    if (!this.#buyButtonsIntersectionObserver) {
      this.#setupIntersectionObserver();
      return;
    }

    this.#buyButtonsIntersectionObserver.disconnect();
    this.#observedBuyButtons = buyButtonsBlock;
    this.#observedTarget = observeTarget;
    this.#buyButtonsIntersectionObserver.observe(observeTarget);
    this.#setupMainButtonObserver(buyButtonsBlock);
    this.#syncStickyVisibility(observeTarget, this.#isTargetInView(observeTarget));
  }

  /**
   * Clones the live main add-to-cart button into the sticky bar.
   */
  #mirrorMainButton() {
    const mainButton = this.#getMainAddToCartButton();
    const mount = this.refs.ctaMount;
    if (!mainButton || !mount) return;

    const clone = /** @type {HTMLButtonElement} */ (mainButton.cloneNode(true));
    clone.type = 'button';
    clone.removeAttribute('ref');
    clone.removeAttribute('name');
    clone.removeAttribute('data-added');
    clone.classList.add('sticky-add-to-cart__button');
    clone.disabled = false;
    clone.setAttribute('aria-disabled', mainButton.disabled ? 'true' : 'false');
    clone.classList.toggle('is-disabled', mainButton.disabled);
    clone.setAttribute('on:click', '/handleAddToCartClick');

    mount.replaceChildren(clone);
  }

  /**
   * @param {HTMLElement} target
   * @returns {boolean}
   */
  #isTargetInView(target) {
    const rect = target.getBoundingClientRect();
    const bottomLimit = window.innerHeight - this.#getBottomObscuredHeight();
    return rect.top < bottomLimit && rect.bottom > 0;
  }

  /**
   * @param {HTMLElement} _target
   * @param {boolean} targetInView - Whether the ATC (or buy-buttons) is usable in the viewport
   */
  #syncStickyVisibility(_target, targetInView) {
    if (!this.#mobileMediaQuery.matches) {
      if (this.#isStuck) this.#hideStickyBar();
      return;
    }

    if (this.#hiddenByBottom) return;

    if (!targetInView) {
      this.#showStickyBar();
      return;
    }

    if (this.#isStuck) {
      this.#hideStickyBar();
    }
  }

  #handleMobileMediaChange = () => {
    if (!this.#mobileMediaQuery.matches) {
      if (this.#isStuck) this.#hideStickyBar();
      return;
    }

    this.#rebuildBuyButtonsObserver();
  };

  handleAddToCartClick = () => {
    const mainButton = this.#getMainAddToCartButton();
    if (!mainButton) return;

    const wasDisabled = mainButton.disabled;
    if (wasDisabled) mainButton.disabled = false;

    mainButton.dataset.puppet = 'true';
    mainButton.click();
    delete mainButton.dataset.puppet;

    if (wasDisabled) mainButton.disabled = true;
  };

  #showStickyBar() {
    if (!this.#mobileMediaQuery.matches) return;

    this.#isStuck = true;
    this.refs.stickyBar.dataset.stuck = 'true';
    this.#mirrorMainButton();
  }

  #hideStickyBar() {
    this.#isStuck = false;
    this.refs.stickyBar.dataset.stuck = 'false';
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getBuyButtonsBlock() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return null;

    const sectionRoot = document.getElementById(`shopify-section-${sectionId}`);
    return sectionRoot?.querySelector('.buy-buttons-block') ?? null;
  }

  /**
   * @param {HTMLElement} buyButtons
   * @returns {HTMLButtonElement | null}
   */
  #getMainAddToCartButtonFrom(buyButtons) {
    const byTestId = buyButtons.querySelector('[data-testid="standalone-add-to-cart"]');
    if (byTestId instanceof HTMLButtonElement) return byTestId;

    const byRef = buyButtons.querySelector('[ref="addToCartButton"]');
    if (byRef instanceof HTMLButtonElement) return byRef;

    return null;
  }

  /**
   * @returns {HTMLButtonElement | null}
   */
  #getMainAddToCartButton() {
    const buyButtons = this.#observedBuyButtons ?? this.#getBuyButtonsBlock();
    if (!buyButtons) return null;

    return this.#getMainAddToCartButtonFrom(buyButtons);
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
